import styled from 'styled-components';
import Link from 'next/link';

import { KnowledgeChannel } from '../../utils/knowledge/types';
import { getKnowledgeTheme } from '../../utils/knowledge/knowledgeTheme';

type ChannelProps = { $channel: KnowledgeChannel };

const lato = "'Lato', sans-serif";

export const KnowledgeCentreRoot = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: ${lato};
  font-size: 14px;
  line-height: 1.6;
  color: #333333;
  background: #ffffff;
`;

export const KnowledgeCentreHeader = styled.header<ChannelProps>`
  max-width: ${({ $channel }) => getKnowledgeTheme($channel).maxWidth};
  margin: 0 auto;
  padding: 24px 20px 16px;
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
`;

export const KnowledgeCentreLogoLink = styled(Link)<ChannelProps>`
  display: block;
  width: 50px;
  height: 48px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  flex-shrink: 0;
  background-image: url(${({ href }) => ''});
`;

export const KnowledgeCentreLogo = styled(Link)<{ $logoSrc: string } & ChannelProps>`
  display: block;
  width: 50px;
  height: 48px;
  background-image: url(${({ $logoSrc }) => $logoSrc});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  flex-shrink: 0;
`;

export const KnowledgeSearchBar = styled.div`
  flex: 1;
  display: flex;
  max-width: 680px;
`;

export const KnowledgeSearchInput = styled.input<ChannelProps>`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid ${({ $channel }) => getKnowledgeTheme($channel).accent};
  font-size: 14px;
  font-family: ${lato};
  outline: none;

  &:focus {
    box-shadow: 0 0 0 2px
      ${({ $channel }) =>
        $channel === 'help' ? 'rgba(254, 194, 0, 0.3)' : 'rgba(234, 239, 216, 0.8)'};
  }
`;

export const KnowledgeSearchButton = styled.button<ChannelProps>`
  padding: 12px 28px;
  background: ${({ $channel }) => getKnowledgeTheme($channel).btnBg};
  color: ${({ $channel }) => getKnowledgeTheme($channel).btnText};
  border: 1px solid ${({ $channel }) => getKnowledgeTheme($channel).accent};
  border-left: none;
  font-family: ${lato};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.15s ease;

  &:hover {
    opacity: 0.85;
  }

  &:focus {
    box-shadow: 0 0 0 3px
      ${({ $channel }) =>
        $channel === 'help' ? 'rgba(254, 194, 0, 0.4)' : 'rgba(234, 239, 216, 0.9)'};
    outline: none;
  }
`;

export const KnowledgeCentreNav = styled.nav<ChannelProps>`
  max-width: ${({ $channel }) => getKnowledgeTheme($channel).maxWidth};
  margin: 0 auto;
  padding: 0 20px;
  border-bottom: 1px solid #eee;
  width: 100%;
`;

export const KnowledgeCentreNavList = styled.ul`
  list-style: none;
  display: flex;
  gap: 24px;
  padding: 12px 0;
  margin: 0;
`;

export const KnowledgeCentreNavLink = styled(Link)<ChannelProps>`
  text-decoration: none;
  color: ${({ $channel }) => getKnowledgeTheme($channel).heading};
  font-family: ${lato};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: color 0.15s ease;

  &:hover,
  &:focus {
    color: ${({ $channel }) => getKnowledgeTheme($channel).accent};
  }

  &.nav-home {
    font-size: 18px;
  }
`;

export const KnowledgeSearchResults = styled.div<ChannelProps>`
  max-width: ${({ $channel }) => getKnowledgeTheme($channel).maxWidth};
  margin: 16px auto 0;
  padding: 0 20px;
  width: 100%;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    padding: 6px 0;
  }
`;

export const KnowledgeSearchResultLink = styled(Link)`
  color: #0066cc;
  text-decoration: underline;
  font-size: 13px;
  transition: color 0.15s ease;

  &:hover,
  &:focus {
    color: #135f90;
  }
`;

export const KnowledgePageLayout = styled.div<ChannelProps>`
  max-width: ${({ $channel }) => getKnowledgeTheme($channel).maxWidth};
  margin: 0 auto;
  padding: 32px 20px;
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 48px;
  width: 100%;
  flex: 1;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const KnowledgeMainColumn = styled.div``;

export const KnowledgeSidebar = styled.aside`
  h2 {
    font-family: ${lato};
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    color: #111111;
    margin: 0 0 12px;
  }
`;

export const KnowledgeSidebarList = styled.ul`
  list-style: none;
  margin: 0 0 24px;
  padding: 0;
`;

export const KnowledgeSidebarItem = styled.li`
  padding: 8px 0;
  border-bottom: 1px solid #eee;
`;

export const KnowledgeSidebarLink = styled(Link)`
  color: #0066cc;
  text-decoration: underline;
  font-size: 13px;
  transition: color 0.15s ease;

  &:hover,
  &:focus {
    color: #135f90;
  }
`;

export const KnowledgeContactButton = styled.a<ChannelProps>`
  display: block;
  text-align: center;
  background: ${({ $channel }) => getKnowledgeTheme($channel).btnBg};
  color: ${({ $channel }) => getKnowledgeTheme($channel).btnText};
  padding: 14px 24px;
  text-decoration: none;
  font-family: ${lato};
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 2px;
  margin-top: 16px;
  transition: all 0.15s ease;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
    color: ${({ $channel }) => getKnowledgeTheme($channel).btnText};
  }
`;

export const KnowledgeIndexHeading = styled.h1`
  font-family: ${lato};
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  color: #111111;
  margin: 0 0 24px;
`;

export const KnowledgeCategoryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const KnowledgeTopicSection = styled.section`
  margin-bottom: 32px;
`;

export const KnowledgeTopicHeading = styled.h2`
  font-family: ${lato};
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  color: #111111;
  margin: 0 0 12px;
  padding-bottom: 4px;
  border-bottom: 1px solid #111111;
  display: inline-block;

  a {
    color: #0066cc;
    text-decoration: underline;
    transition: color 0.15s ease;

    &:hover,
    &:focus {
      color: #135f90;
    }
  }
`;

export const KnowledgeTopicList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const KnowledgeTopicItem = styled.li`
  padding: 6px 0;
`;

export const KnowledgeTopicLink = styled(Link)`
  color: #0066cc;
  text-decoration: underline;
  font-size: 13px;
  transition: color 0.15s ease;

  &:hover,
  &:focus {
    color: #135f90;
  }
`;

export const KnowledgeViewAllLink = styled(Link)<ChannelProps>`
  margin-top: 8px;
  display: inline-block;
  color: ${({ $channel }) => getKnowledgeTheme($channel).accent};
  font-size: 13px;
  font-weight: 700;
  text-decoration: underline;
`;

export const KnowledgeBreadcrumbNav = styled.nav`
  font-size: 12px;
  color: #999;
  margin-bottom: 24px;
`;

export const KnowledgeBreadcrumbLink = styled(Link)`
  color: #999;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const KnowledgeBreadcrumbSep = styled.span`
  margin: 0 6px;
`;

export const KnowledgeBreadcrumbCurrent = styled.span`
  color: #999;
`;

export const KnowledgeArticleDetailTitle = styled.h1`
  font-family: ${lato};
  font-size: 28px;
  font-weight: 400;
  color: #111111;
  margin: 0 0 24px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eee;
`;

export const KnowledgeCategoryPageTitle = styled.h1`
  font-family: ${lato};
  font-size: 20px;
  font-weight: 700;
  text-transform: uppercase;
  color: #111111;
  margin: 0 0 24px;
`;

export const KnowledgeFullArticleList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const KnowledgeFullArticleItem = styled.li`
  padding: 10px 0;
  border-bottom: 1px solid #eee;
`;

export const KnowledgeFullArticleLink = styled(Link)`
  color: #0066cc;
  text-decoration: underline;
  font-size: 14px;
  transition: color 0.15s ease;

  &:hover,
  &:focus {
    color: #135f90;
  }
`;

export const KnowledgeArticleBody = styled.div`
  line-height: 1.8;
  font-size: 14px;
  max-width: 72rem;

  > :first-child {
    margin-top: 0;
  }

  h2,
  h3 {
    font-family: ${lato};
    margin: 24px 0 12px;
    font-weight: 700;
    color: #111111;
  }

  h3 {
    font-size: 16px;
  }

  p {
    margin: 0 0 16px;
    text-align: left;
  }

  p:empty {
    display: none;
  }

  ul,
  ol {
    margin: 0 0 16px;
    padding-left: 24px;
    text-align: left;
  }

  li {
    margin: 4px 0;
    display: list-item;
  }

  li > p,
  li > span {
    display: inline;
    margin: 0;
  }

  a {
    color: #0066cc;
    text-decoration: underline;

    &:hover,
    &:focus {
      color: #135f90;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    margin: 16px 0;
    border-radius: 4px;
  }

  img.knowledge-profile-example {
    width: 257px;
    max-width: min(257px, 48%);
    height: auto;
    margin: 16px 16px 16px 0;
    vertical-align: top;
    object-fit: contain;
  }

  strong,
  b {
    font-weight: 700;
  }
`;

export const KnowledgeCentreFooter = styled.footer<ChannelProps>`
  max-width: ${({ $channel }) => getKnowledgeTheme($channel).maxWidth};
  margin: 48px auto 0;
  padding: 24px 20px;
  border-top: 2px solid ${({ $channel }) => getKnowledgeTheme($channel).accent};
  text-align: center;
  color: #999;
  font-size: 12px;
  width: 100%;
`;
